import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, TextField, Tooltip, Typography } from '@mui/material';
import Iconify from '../Iconify';
import VideoFileDropzone from '../video/VideoFileDropzone';
import VideoSourceFields from '../video/VideoSourceFields';
import { EntityImageEditor } from '../entityImage/EntityImageEditor';
import { parseVideoLink, VIDEO_PROVIDER_LABELS } from '../../utils/videoSource';
import {
  formatMediaDate,
  formatMediaSize,
  nextGalleryLabel,
} from '../../utils/contentMediaUtils';
import {
  createGalleryDocument,
  createGalleryPhoto,
  createGalleryVideoFile,
  createGalleryVideoLink,
  deleteGalleryItem,
  fetchGalleryPhotoSource,
  reorderGallery,
  updateGalleryLabel,
  updateGalleryPhoto,
} from './contentGalleryApi';
import { CustomErrorLogger } from '../../utils/CustomErrorLogger';
import OrderMoveButtons from '../listOrder/OrderMoveButtons';
import { UPLOAD_LIMITS } from '../../utils/uploadLimits';

const MAX_PHOTO_BYTES = 25 * 1024 * 1024;
const PHOTO_MIMES = ['image/jpeg', 'image/png'];
const PDF_MIME = 'application/pdf';
const VIDEO_MIMES = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];

const pictoFor = (kind) => (kind === 'video' ? 'mdi:video' : 'bxs:file-pdf');

const ContentMediaGallery = ({
  scope = 'article',
  parentId = null,
  items = [],
  localItems = [],
  onLocalItemsChange,
  onPersistedChange,
  readOnly = false,
  onOpen,
  maxDocumentVideoBytes = 100 * 1024 * 1024,
  maxVideoBytes = UPLOAD_LIMITS.videoBytes,
}) => {
  const [errMsg, setErrMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [panel, setPanel] = useState(null);
  const [videoKind, setVideoKind] = useState('upload');
  const [videoUrl, setVideoUrl] = useState('');
  const [queue, setQueue] = useState([]);
  const [editorSource, setEditorSource] = useState(null);
  const [openEditor, setOpenEditor] = useState(false);
  const [editingTarget, setEditingTarget] = useState(null);
  const [pendingOrder, setPendingOrder] = useState(null);

  const maxMo = Math.round(maxDocumentVideoBytes / (1024 * 1024));
  const videoMo = Math.round(maxVideoBytes / (1024 * 1024));
  const cardKey = (card) => card.id || card.localId;
  const sourceCards = parentId || readOnly ? items : localItems;
  const cards = pendingOrder
    ? pendingOrder.map((id) => sourceCards.find((card) => cardKey(card) === id)).filter(Boolean)
    : sourceCards;

  useEffect(() => {
    if (!pendingOrder) {
      return;
    }
    const sourceIds = sourceCards.map(cardKey).join('|');
    if (sourceIds === pendingOrder.join('|')) {
      setPendingOrder(null);
    }
  }, [pendingOrder, sourceCards]);

  useEffect(() => {
    if (openEditor || !queue.length) {
      return;
    }
    const [next, ...rest] = queue;
    setQueue(rest);
    setEditingTarget(null);
    setEditorSource({ file: next, src: URL.createObjectURL(next) });
    setOpenEditor(true);
  }, [queue, openEditor]);

  const persistOrStore = useCallback(async (entry) => {
    if (!parentId) {
      onLocalItemsChange?.([...(localItems || []), entry]);
      return;
    }
    setBusy(true);
    try {
      if (entry.kind === 'photo') {
        await createGalleryPhoto(scope, parentId, entry.prepared, entry.label);
      } else if (entry.kind === 'document') {
        await createGalleryDocument(scope, parentId, entry.file, entry.label);
      } else if (entry.file) {
        await createGalleryVideoFile(scope, parentId, entry.file, entry.label);
      } else {
        await createGalleryVideoLink(scope, parentId, entry.externalUrl, entry.label);
      }
      await onPersistedChange?.();
    } catch (error) {
      setErrMsg(error.response?.data?.message || 'Impossible d’ajouter ce média.');
      CustomErrorLogger(error, 'ContentMediaGallery.add');
    } finally {
      setBusy(false);
    }
  }, [localItems, onLocalItemsChange, onPersistedChange, parentId, scope]);

  const acceptPhotos = (files) => {
    const list = Array.from(files || []);
    const rejected = list.find((file) => !PHOTO_MIMES.includes(file.type) || file.size > MAX_PHOTO_BYTES);
    if (rejected) {
      setErrMsg('Formats autorisés : JPEG ou PNG, 25 Mo maximum par photo.');
      return;
    }
    setErrMsg(null);
    setQueue((previous) => [...previous, ...list]);
  };

  const acceptDocument = async (files) => {
    const file = Array.from(files || [])[0];
    if (!file) {
      return;
    }
    if (file.type !== PDF_MIME || file.size > maxDocumentVideoBytes) {
      setErrMsg(`Format autorisé : PDF jusqu’à ${maxMo} Mo.`);
      return;
    }
    setErrMsg(null);
    await persistOrStore({
      localId: `local-${Date.now()}`,
      kind: 'document',
      label: nextGalleryLabel('document', cards),
      originalFilename: file.name,
      sizeBytes: file.size,
      createdAt: new Date().toISOString(),
      file,
    });
  };

  const acceptVideoFile = async (files) => {
    const file = Array.from(files || [])[0];
    if (!file) {
      return;
    }
    if (!VIDEO_MIMES.includes(file.type) || file.size > maxVideoBytes) {
      setErrMsg(`Formats vidéo : MP4, MOV, AVI, WebM jusqu’à ${videoMo} Mo.`);
      return;
    }
    setErrMsg(null);
    await persistOrStore({
      localId: `local-${Date.now()}`,
      kind: 'video',
      label: nextGalleryLabel('video', cards),
      originalFilename: file.name,
      sizeBytes: file.size,
      createdAt: new Date().toISOString(),
      file,
    });
  };

  const addVideoLink = async () => {
    const parsed = parseVideoLink(videoUrl);
    if (!parsed) {
      setErrMsg('Lien vidéo non reconnu.');
      return;
    }
    setErrMsg(null);
    await persistOrStore({
      localId: `local-${Date.now()}`,
      kind: 'video',
      label: nextGalleryLabel('video', cards),
      originalFilename: '',
      sizeBytes: 0,
      createdAt: new Date().toISOString(),
      externalUrl: parsed.externalUrl,
      providerLabel: VIDEO_PROVIDER_LABELS[parsed.provider] || '',
    });
    setVideoUrl('');
    setPanel(null);
  };

  const handleCropConfirm = async (prepared) => {
    setBusy(true);
    try {
      if (editingTarget?.photoId) {
        await updateGalleryPhoto(scope, editingTarget.photoId, prepared);
        await onPersistedChange?.();
      } else if (editingTarget?.localId) {
        onLocalItemsChange?.(localItems.map((entry) => (
          entry.localId === editingTarget.localId
            ? { ...entry, prepared, previewUrl: URL.createObjectURL(prepared.croppedFile) }
            : entry
        )));
      } else {
        const file = prepared.originalFile || prepared.croppedFile;
        await persistOrStore({
          localId: `local-${Date.now()}`,
          kind: 'photo',
          label: nextGalleryLabel('photo', cards),
          originalFilename: file?.name || '',
          sizeBytes: file?.size || 0,
          createdAt: new Date().toISOString(),
          prepared,
          previewUrl: URL.createObjectURL(prepared.croppedFile),
        });
      }
    } catch (error) {
      setErrMsg('Erreur lors de l’enregistrement de la photo.');
      CustomErrorLogger(error, 'ContentMediaGallery.crop');
    } finally {
      setBusy(false);
      setOpenEditor(false);
      setEditingTarget(null);
    }
  };

  const changeLabel = async (card, label) => {
    const trimmed = label.trim();
    if (!trimmed || trimmed === card.label) {
      return;
    }
    if (!parentId) {
      onLocalItemsChange?.(localItems.map((entry) => (
        entry.localId === card.localId ? { ...entry, label: trimmed } : entry
      )));
      return;
    }
    setBusy(true);
    try {
      await updateGalleryLabel(scope, card.id, trimmed);
      await onPersistedChange?.();
    } catch (error) {
      setErrMsg('Impossible de modifier le libellé.');
      CustomErrorLogger(error, 'ContentMediaGallery.label');
    } finally {
      setBusy(false);
    }
  };

  const removeCard = async (card) => {
    if (!parentId) {
      if (card.previewUrl) {
        URL.revokeObjectURL(card.previewUrl);
      }
      onLocalItemsChange?.(localItems.filter((entry) => entry.localId !== card.localId));
      return;
    }
    setBusy(true);
    try {
      await deleteGalleryItem(scope, card.id);
      await onPersistedChange?.();
    } catch (error) {
      setErrMsg('Impossible de supprimer ce média.');
      CustomErrorLogger(error, 'ContentMediaGallery.delete');
    } finally {
      setBusy(false);
    }
  };

  const recrop = async (card) => {
    if (card.localId) {
      const file = card.prepared?.originalFile || card.prepared?.croppedFile;
      if (!file) {
        return;
      }
      setEditingTarget({ localId: card.localId });
      setEditorSource({ file, src: URL.createObjectURL(file) });
      setOpenEditor(true);
      return;
    }
    setBusy(true);
    try {
      const src = await fetchGalleryPhotoSource(scope, card.photo);
      if (!src) {
        setErrMsg('Image source indisponible.');
        return;
      }
      setEditingTarget({ photoId: card.photoId });
      setEditorSource({ file: null, src });
      setOpenEditor(true);
    } finally {
      setBusy(false);
    }
  };

  const moveCard = async (key, direction) => {
    const next = [...cards];
    const from = next.findIndex((card) => (card.id || card.localId) === key);
    const to = direction === 'left' ? from - 1 : from + 1;
    if (from < 0 || to < 0 || to >= next.length) {
      return;
    }
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    if (!parentId) {
      onLocalItemsChange?.(next);
      return;
    }
    const previousOrder = cards.map(cardKey);
    setPendingOrder(next.map(cardKey));
    try {
      await reorderGallery(scope, parentId, next.map((card) => card.id));
      await onPersistedChange?.();
    } catch (error) {
      setPendingOrder(previousOrder);
      setErrMsg('Impossible de changer l’ordre.');
      CustomErrorLogger(error, 'ContentMediaGallery.order');
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {errMsg && <Alert severity="error" sx={{ mb: 1 }}>{errMsg}</Alert>}
      {!readOnly && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          <Button size="small" variant={panel === 'photo' ? 'contained' : 'outlined'} onClick={() => setPanel(panel === 'photo' ? null : 'photo')}>Photo</Button>
          <Button size="small" variant={panel === 'document' ? 'contained' : 'outlined'} onClick={() => setPanel(panel === 'document' ? null : 'document')}>Document</Button>
          <Button size="small" variant={panel === 'video' ? 'contained' : 'outlined'} onClick={() => setPanel(panel === 'video' ? null : 'video')}>Vidéo</Button>
        </Box>
      )}
      {!readOnly && panel === 'photo' && (
        <VideoFileDropzone multiple accept="image/png,image/jpeg" hint="JPEG ou PNG, 25 Mo maximum" onFiles={acceptPhotos} />
      )}
      {!readOnly && panel === 'document' && (
        <VideoFileDropzone accept="application/pdf,.pdf" hint={`PDF jusqu’à ${maxMo} Mo`} onFiles={acceptDocument} />
      )}
      {!readOnly && panel === 'video' && (
        <Box sx={{ mb: 1 }}>
          <VideoSourceFields
            sourceKind={videoKind}
            onSourceKindChange={setVideoKind}
            url={videoUrl}
            onUrlChange={setVideoUrl}
            fileHint={`MP4, MOV, AVI ou WebM jusqu’à ${videoMo} Mo`}
          >
            <VideoFileDropzone accept="video/mp4,video/quicktime,video/x-msvideo,video/webm" hint={`MP4, MOV, AVI ou WebM jusqu’à ${videoMo} Mo`} onFiles={acceptVideoFile} />
          </VideoSourceFields>
          {videoKind === 'link' && (
            <Button sx={{ mt: 1 }} variant="outlined" onClick={addVideoLink}>Ajouter cette vidéo</Button>
          )}
        </Box>
      )}
      {busy && <CircularProgress size={16} sx={{ my: 1 }} />}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
        {cards.map((card, index) => {
          const key = card.id || card.localId;
          const addedOn = formatMediaDate(card.createdAt);
          const sizeLabel = card.kind === 'video' && card.externalUrl
            ? `Lien · ${card.providerLabel || 'lien'}`
            : formatMediaSize(card.sizeBytes);
          return (
            <Box
              key={key}
              onClick={() => readOnly && onOpen?.(index)}
              sx={{
                width: 220,
                p: 1.25,
                borderRadius: '12px',
                backgroundColor: '#fff',
                border: '1px solid #E6E6E6',
                cursor: readOnly ? 'pointer' : 'default',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ height: 120, borderRadius: '8px', bgcolor: '#F6F6F6', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', mb: 1 }}>
                {card.kind === 'photo' && (card.thumbUrl || card.previewUrl) ? (
                  <Box component="img" src={card.previewUrl || card.thumbUrl} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <Iconify icon={pictoFor(card.kind)} width={42} height={42} />
                )}
              </Box>
              {readOnly ? (
                <Typography sx={{ fontFamily: 'Mulish', fontWeight: 700, fontSize: '10.8px' }}>{card.label}</Typography>
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  defaultValue={card.label}
                  key={`${key}-${card.label}`}
                  onBlur={(event) => changeLabel(card, event.target.value)}
                  onClick={(event) => event.stopPropagation()}
                  inputProps={{ 'aria-label': 'Libellé du média' }}
                  sx={{ '& .MuiInputBase-input': { fontSize: '12.3px', fontFamily: 'Mulish' } }}
                />
              )}
              {(addedOn || sizeLabel) && (
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.75 }}>
                  <Typography noWrap sx={{ fontFamily: 'Mulish', fontSize: 12, color: '#6E6D6D', flex: 1, minWidth: 0 }}>
                    {addedOn}
                  </Typography>
                  <Typography noWrap sx={{ fontFamily: 'Mulish', fontSize: 12, color: '#6E6D6D', flexShrink: 0 }}>
                    {sizeLabel}
                  </Typography>
                </Box>
              )}
              {card.originalFilename && (
                <Tooltip title={card.originalFilename} enterTouchDelay={0}>
                  <Typography noWrap sx={{ fontFamily: 'Mulish', fontSize: 12, color: '#6E6D6D', mt: 0.25 }}>
                    {card.originalFilename}
                  </Typography>
                </Tooltip>
              )}
              {!readOnly && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5, mt: 'auto', pt: 0.5 }}>
                  <OrderMoveButtons
                    axis="horizontal"
                    disableUp={index === 0}
                    disableDown={index === cards.length - 1}
                    onMove={(direction) => moveCard(key, direction)}
                  />
                  {card.kind === 'photo' && !card.legacyPhoto && (
                    <Button size="small" aria-label="Recadrer" onClick={() => recrop(card)} sx={{ minWidth: 0, p: 0.5 }}>
                      <Iconify icon="ic:outline-edit" width={18} />
                    </Button>
                  )}
                  <Button size="small" aria-label="Supprimer" onClick={() => removeCard(card)} sx={{ minWidth: 0, p: 0.5, color: '#F12525' }}>
                    <Iconify icon="ic:outline-delete" width={18} />
                  </Button>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
      {openEditor && (
        <EntityImageEditor
          entityType="articlePhoto"
          mode="local"
          initialFile={editorSource?.file}
          initialImageSrc={editorSource?.src}
          modifyPhoto={openEditor}
          setModifyPhoto={(open) => {
            if (!open) {
              setOpenEditor(false);
              setEditingTarget(null);
            }
          }}
          onLocalConfirm={handleCropConfirm}
          dialogTitle="Préparer la photo"
        />
      )}
    </Box>
  );
};

export default ContentMediaGallery;
